import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/nurseryApi";
import { CreditCard, ArrowSquareOut, Star, Pencil } from "@phosphor-icons/react";
import BackToOverview from "../../components/BackToOverview";

const PLANS = [
  { key: "founding", label: "Founding (free)", monthly: 0, color: "var(--accent)" },
  { key: "starter", label: "Starter", monthly: 199, color: "var(--primary)" },
  { key: "pro", label: "Pro", monthly: 399, color: "var(--secondary)" },
  { key: "enterprise", label: "Enterprise", monthly: 1299, color: "#7B3F3F" },
  { key: "none", label: "Unassigned", monthly: 0, color: "var(--muted-fg)" },
];

const planMeta = (key) => PLANS.find((p) => p.key === key) || PLANS[PLANS.length - 1];

export default function AdminSubscriptions() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data.filter((u) => !u.is_admin)));
  useEffect(() => { load(); }, []);

  const byPlan = {};
  PLANS.forEach((p) => { byPlan[p.key] = []; });
  users.forEach((u) => {
    const k = u.plan && byPlan[u.plan] ? u.plan : "none";
    byPlan[k].push(u);
  });

  const mrr = users.reduce((sum, u) => sum + planMeta(u.plan || "none").monthly, 0);
  const paying = users.filter((u) => ["starter", "pro", "enterprise"].includes(u.plan)).length;

  const savePlan = async (uid, plan) => {
    await api.put(`/admin/users/${uid}`, { plan });
    setEditing(null);
    load();
  };

  return (
    <div data-testid="admin-subscriptions-page" className="fade-up">
      <BackToOverview />
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Subscription book</div>
      <h1 className="page-title" style={{ marginTop: 8 }}>Subscriptions</h1>
      <p style={{ color: "var(--muted-fg)", marginTop: 6, fontSize: 14.5 }}>Assign a tier to each nursery. MRR is calculated from active paying tiers.</p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginTop: 24 }}>
        <KPI label="MRR" value={`$${mrr.toLocaleString()}`} hint="Sum of monthly plans" />
        <KPI label="Paying nurseries" value={paying} hint="Starter + Pro + Enterprise" />
        <KPI label="Founding nurseries" value={byPlan.founding.length} hint="Free during field test" />
        <KPI label="Unassigned" value={byPlan.none.length} hint="No plan set yet" />
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted-fg)", marginTop: 32, marginBottom: 12 }}>By plan</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        {PLANS.map((p) => (
          <div key={p.key} className="card" style={{ padding: 18 }} data-testid={`plan-col-${p.key}`}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: p.color }}>{p.label}</div>
                <div className="font-display" style={{ fontSize: 28, marginTop: 4 }}>
                  {p.monthly > 0 ? `$${p.monthly}` : "—"}<span style={{ fontSize: 12, color: "var(--muted-fg)" }}>/mo</span>
                </div>
              </div>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: "var(--bg-alt)", display: "grid", placeItems: "center" }}>
                <CreditCard size={20} weight="bold" color={p.color} />
              </div>
            </div>
            <div style={{ fontSize: 13, color: "var(--muted-fg)", marginTop: 8 }}>{byPlan[p.key].length} nursery{byPlan[p.key].length === 1 ? "" : "ies"}</div>
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
              {byPlan[p.key].length === 0 && <div style={{ fontSize: 13, color: "var(--muted-fg)", fontStyle: "italic" }}>No nurseries on this plan.</div>}
              {byPlan[p.key].map((u) => (
                <div key={u.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", background: "var(--bg-alt)", borderRadius: 10 }}>
                  <Link to={`/admin/users/${u.id}`} style={{ textDecoration: "none", color: "var(--text)", flex: 1, minWidth: 0 }} data-testid={`sub-nursery-${u.id}`}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.is_founding && <Star size={12} weight="fill" color="var(--accent)" />}
                      {u.nursery_name}
                    </div>
                    <div style={{ fontSize: 11.5, color: "var(--muted-fg)" }}>{u.email}</div>
                  </Link>
                  <button className="btn btn-ghost" style={{ minHeight: 30, padding: "4px 8px" }} onClick={() => setEditing(u)} title="Change plan" data-testid={`change-plan-${u.id}`}>
                    <Pencil size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={`Set plan · ${editing.nursery_name}`}>
          <div style={{ display: "grid", gap: 10 }}>
            {PLANS.map((p) => (
              <button
                key={p.key}
                className="btn btn-outline"
                onClick={() => savePlan(editing.id, p.key)}
                data-testid={`assign-plan-${p.key}`}
                style={{ justifyContent: "space-between", padding: "14px 16px", textAlign: "left" }}
              >
                <span>
                  <b>{p.label}</b>
                  <div style={{ fontSize: 12, color: "var(--muted-fg)", fontWeight: 400 }}>
                    {p.monthly > 0 ? `$${p.monthly}/mo` : "No monthly charge"}
                  </div>
                </span>
                <ArrowSquareOut size={14} weight="bold" />
              </button>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

function KPI({ label, value, hint }) {
  return (
    <div className="stat-tile">
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted-fg)" }}>{label}</div>
      <div className="font-display" style={{ fontSize: 36, marginTop: 8, lineHeight: 1 }}>{value}</div>
      {hint && <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--muted-fg)" }}>{hint}</div>}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(42,51,42,0.45)", backdropFilter: "blur(4px)", display: "grid", placeItems: "center", zIndex: 60, padding: 16 }} onClick={onClose}>
      <div className="card-soft" onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface)", padding: 24, width: "100%", maxWidth: 520, maxHeight: "90vh", overflow: "auto", borderRadius: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 className="font-display" style={{ fontSize: 22 }}>{title}</h3>
          <button className="btn btn-ghost" onClick={onClose} data-testid="modal-close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
